import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const LandingPage = () => {
  const navigate = useNavigate();

  const painPoints = [
    'Precificando com base no concorrente',
    'Sem saber sua margem real',
    'Criando kits sem estratégia',
    'Ajustando preço no "feeling"',
    'Trabalhando muito e lucrando pouco',
  ];

  const valueProps = [
    { icon: 'Calculator', text: 'Simula lucro antes de publicar' },
    { icon: 'TrendingUp', text: 'Descobre margem ideal por produto' },
    { icon: 'Package', text: 'Cria kits para aumentar ticket médio' },
    { icon: 'Sparkles', text: 'Gera descrições profissionais com IA' },
    { icon: 'BarChart3', text: 'Toma decisões baseadas em dados' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/assets/images/Captura_de_tela_2026-02-06_014642-1770355644537.png" 
              alt="VERTEX COMMERCE Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-bold text-xl text-gray-900">VERTEX COMMERCE</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/pricing')}>
              Ver Planos
            </Button>
            <Button variant="outline" onClick={() => navigate('/login')}>
              Entrar
            </Button>
            <Button onClick={() => navigate('/pricing')}>
              Começar Agora
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Venda com margem. <br />
            <span className="text-blue-600">Pare de precificar no improviso!</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            A plataforma estratégica para vendedores de marketplace que querem <br />
            <strong>aumentar lucro, criar kits inteligentes e dominar a precificação.</strong>
          </p>
          <p className="text-lg text-red-600 font-semibold mb-10">
            Se você já vende, está perdendo dinheiro sem perceber.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="xl" onClick={() => navigate('/pricing')} className="w-full sm:w-auto">
              <Icon name="Zap" size={20} />
              Começar agora por R$97/mês
            </Button>
            <Button size="xl" variant="outline" onClick={() => navigate('/pricing')} className="w-full sm:w-auto">
              <Icon name="Play" size={20} />
              Ver como funciona
            </Button>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Você reconhece isso?
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {painPoints?.map((point, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                  <Icon name="AlertCircle" size={20} className="text-red-600 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">{point}</p>
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-xl text-gray-700 mb-4">
                Isso não é falta de esforço. <strong>É falta de sistema estratégico.</strong>
              </p>
              <p className="text-lg text-blue-600 font-semibold">
                A Vertex Commerce foi criada para transformar vendedores comuns em operadores profissionais.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Com a Vertex Commerce você:
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {valueProps?.map((prop, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={prop?.icon} size={24} className="text-blue-600" />
                  </div>
                  <p className="text-lg text-gray-700 mt-2">{prop?.text}</p>
                </div>
              ))}
            </div>
            <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-2xl">
              <p className="text-2xl font-bold mb-2">
                Não é sobre organizar pedidos.
              </p>
              <p className="text-xl">
                É sobre <span className="underline">aumentar margem</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Section */}
      <section className="bg-gray-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prova de Posicionamento
            </h2>
            <p className="text-xl mb-4">
              Enquanto ERPs tradicionais focam em controle operacional, <br />
              <strong className="text-blue-400">nós focamos em crescimento e lucratividade.</strong>
            </p>
            <p className="text-lg text-gray-300">
              Aqui você não apenas registra vendas. <br />
              Você estrutura sua operação para escalar.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Teaser Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Modelo de Preço – Posicionamento Psicológico
            </h2>
            <p className="text-xl text-gray-700 mb-4">
              Um sistema que pode aumentar sua margem em 5% já paga a mensalidade.
            </p>
            <p className="text-2xl font-bold text-blue-600 mb-8">
              Você paga R$97. Mas pode ganhar centenas ou milhares a mais em margem estratégica.
            </p>
            <Button size="xl" onClick={() => navigate('/pricing')}>
              <Icon name="ArrowRight" size={20} />
              Ver Planos Completos
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Você pode continuar vendendo no improviso.
            </h2>
            <p className="text-2xl mb-4">
              Ou pode operar com estratégia.
            </p>
            <p className="text-xl mb-8">
              Se você já vende, o plano Profissional é o único que realmente faz sentido.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="xl" variant="secondary" onClick={() => navigate('/pricing')} className="w-full sm:w-auto bg-white text-blue-600 hover:bg-gray-100">
                <Icon name="Zap" size={20} />
                Começar agora por R$97/mês
              </Button>
            </div>
            <p className="text-sm mt-4 text-blue-100">
              Cancele quando quiser.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="container mx-auto px-4 text-center">
          <p>&copy; 2026 Vertex Commerce. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;